import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import type { SystemStyleObject } from '@mui/system';
import type { Theme } from '@mui/material/styles';
import { useUntypedTranslation } from '../../../hooks/useUntypedTranslation';
import {
  AdvisoryComponentId,
  COMPONENT_SCHEMAS,
  AnyCompositeSectionV01,
  CompetitorAnalysisComponentId,
  LayoutPreset,
  ValidationIssue,
  validateCompositeAnswerV01,
} from '../index';
import {
  validateLiveRenderingEnvelopeV01,
  type LiveCompositeAnswerV01,
  type LiveCompositeSectionV01,
  type LiveCompositeSlideV01,
} from '../liveAnswer';
import type {
  AdvisoryBriefAnswerStatus,
  AdvisoryBriefDocumentInputV01,
  AdvisoryBriefQuestionInputV01,
} from './types';
import {
  BusinessModelCanvasRenderer,
  FinancialForecastRenderer,
  RoadmapTimelineRenderer,
  ScenarioMatrixRenderer,
  SystemMapRenderer,
} from './AdvisorySpecializedSections';
import { AdvisorySourcesDrawer } from './AdvisorySourcesDrawer';
import { AdvisoryRecommendationValue } from './AdvisoryRecommendationValue';
import {
  AdvisoryEvidenceText,
  AdvisoryEvidenceValue,
  AdvisoryEvidenceMarkdown,
  AdvisoryInlineEvidenceProvider,
  AdvisoryTableSourceCitations,
} from './AdvisoryInlineEvidence';
import type { AdvisoryQuoteReference } from './advisoryQuoteAnchors';
import {
  buildAdvisoryTableSourceReferences,
  type AdvisoryTableSourceReference,
} from './advisoryTableSources';
import {
  mapAdvisoryAnswerCitations,
  sameAdvisoryPassage,
  type AdvisorySourceReferenceV01,
} from './advisoryProvenance';
import type { AdvisorySourcePassageLoader } from './advisorySourcePassage';
import {
  collectAdvisoryReportEvidence,
  separateAdvisorySourceEntries,
  type AdvisoryAnswerEvidenceEntry,
} from './advisoryReportEvidence';
import { findingsForAnswer } from './advisoryAudit';
import { chapterNeedsAuditAttention } from './advisoryAuditEvidence';

type PlanKind = 'short' | 'long';
type DisplayRow = Record<string, string>;

interface ValidQuestion {
  input: AdvisoryBriefQuestionInputV01;
  answer: LiveCompositeAnswerV01;
  issues: null;
  fallbackText: null;
}

interface InvalidQuestion {
  input: AdvisoryBriefQuestionInputV01;
  answer: null;
  issues: readonly ValidationIssue[];
  fallbackText: null;
}

interface AnswerTextQuestion {
  input: AdvisoryBriefQuestionInputV01;
  answer: null;
  issues: null;
  fallbackText: string;
}

type QuestionState = ValidQuestion | InvalidQuestion | AnswerTextQuestion;

const LABEL_OVERRIDES: Readonly<Record<string, string>> = {
  tam: 'TAM',
  sam: 'SAM',
  x_axis: 'Horizontal axis',
  y_axis: 'Vertical axis',
  if_met: 'If met',
  if_missed: 'If missed',
  owner_or_horizon: 'Owner / horizon',
  current_position: 'Current position',
  source_date: 'Source date',
  role_or_capability: 'Role or capability',
  customer_value: 'Customer value',
  choice_factors: 'Customer choice factors',
  shortlisting_criterion: 'Shortlisting criterion',
  confidence_notes: 'Confidence notes',
  flip_conditions: 'What would change the conclusion',
  smallest_test: 'Smallest decisive test',
  not_established: 'Not established',
  variable_value: 'Variable value',
  outcome_value: 'Outcome value',
};

const humanize = (value: string): string =>
  LABEL_OVERRIDES[value] ?? value.replace(/_/g, ' ').replace(/^./, first => first.toUpperCase());

const briefColumn = (theme: Theme): SystemStyleObject<Theme> => ({
  width: '100%',
  maxWidth: theme.visualPrimitives.briefColumnMaxWidth,
  marginX: 'auto',
});

const briefEvidenceLinkSx: SystemStyleObject<Theme> = {
  textDecoration: 'underline dotted currentColor',
  textDecorationThickness: '1px',
  textUnderlineOffset: '0.25em',
  '&:hover': {
    textDecoration: 'underline dotted currentColor',
  },
};

const briefSpread = (theme: Theme): SystemStyleObject<Theme> => ({
  minHeight: theme.visualPrimitives.briefSpreadMinHeight,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  paddingY: theme.visualPrimitives.briefSpreadPaddingY,
  paddingX: theme.visualPrimitives.briefPageGutter,
  backgroundColor: theme.palette.background.default,
  color: theme.colors.inkPrimary,
});

const eyebrowSx = (theme: Theme): SystemStyleObject<Theme> => ({
  display: 'block',
  color: theme.colors.inkTertiary,
  fontSize: theme.visualPrimitives.eyebrowSize,
  fontWeight: theme.visualPrimitives.eyebrowWeight,
  letterSpacing: theme.visualPrimitives.eyebrowTracking,
  textTransform: 'uppercase',
});

const monoSx = (theme: Theme): SystemStyleObject<Theme> => ({
  fontFamily: theme.visualPrimitives.monoStack,
  fontVariantNumeric: 'tabular-nums',
  letterSpacing: '-0.01em',
});

function AnswerStatusBadge({ status }: { status?: AdvisoryBriefAnswerStatus }) {
  const { t } = useUntypedTranslation();
  if (!status) {
    return null;
  }
  const attention = status === 'partial' || status === 'unanswerable' || status === 'failed';
  return (
    <Typography
      component="span"
      data-answer-status={status}
      sx={theme => ({
        display: 'inline-flex',
        width: 'fit-content',
        color: attention ? theme.colors.vermilion : theme.colors.lavenderDeep,
        border: `1px solid ${attention ? theme.colors.vermilion : theme.visualPrimitives.border}`,
        borderRadius: theme.visualPrimitives.radiusPill,
        paddingX: 1,
        paddingY: 0.2,
        fontSize: 10,
        fontWeight: 500,
        letterSpacing: '0.1em',
        lineHeight: 1.4,
        textTransform: 'uppercase',
      })}
    >
      {t(`advisory:brief.status.${status}`)}
    </Typography>
  );
}

function BriefReveal({ children }: { children: React.ReactNode }) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const element = rootRef.current;
    if (!element || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <Box
      ref={rootRef}
      sx={theme => ({
        opacity: visible ? 1 : 0,
        transform: visible ? 'none' : 'translateY(18px)',
        transition: `opacity 600ms ${theme.visualPrimitives.ease}, transform 600ms ${theme.visualPrimitives.ease}`,
        '@media (prefers-reduced-motion: reduce)': {
          opacity: 1,
          transform: 'none',
          transition: 'none',
        },
      })}
    >
      {children}
    </Box>
  );
}

function UnavailableValue() {
  const { t } = useUntypedTranslation();
  return (
    <Typography
      component="span"
      sx={theme => ({ color: theme.colors.inkTertiary, fontStyle: 'italic' })}
    >
      {t('advisory:brief.notProvided')}
    </Typography>
  );
}

function DisplayValue({ value, prominent = false }: { value: string; prominent?: boolean }) {
  if (value.trim().length === 0) {
    return <UnavailableValue />;
  }
  return (
    <Typography
      sx={theme => ({
        color: prominent ? theme.colors.inkPrimary : theme.colors.inkSecondary,
        fontSize: prominent ? 'clamp(24px, 3.4vw, 38px)' : theme.visualPrimitives.briefBodySize,
        fontWeight: prominent ? 500 : 400,
        letterSpacing: prominent ? '-0.015em' : 'normal',
        lineHeight: prominent ? 1.25 : theme.visualPrimitives.briefBodyLineHeight,
        maxWidth: prominent ? '20em' : '40em',
      })}
    >
      <AdvisoryEvidenceText>{value}</AdvisoryEvidenceText>
    </Typography>
  );
}

interface BriefTableProps {
  caption: string;
  columns: readonly string[];
  rows: readonly DisplayRow[];
  compact: boolean;
  citeTableSources?: boolean;
}

function BriefTable({
  caption,
  columns,
  rows,
  compact,
  citeTableSources = false,
}: BriefTableProps) {
  const { t } = useUntypedTranslation();
  if (rows.length === 0) {
    return (
      <Typography sx={theme => ({ color: theme.colors.inkTertiary, fontStyle: 'italic' })}>
        {t('advisory:brief.knownEmpty')}
      </Typography>
    );
  }

  return (
    <TableContainer
      role="region"
      aria-label={caption}
      tabIndex={0}
      sx={theme => ({
        width: '100%',
        overflowX: 'auto',
        borderTop: `1px solid ${theme.visualPrimitives.border}`,
        '&:focus-visible': {
          outline: `${theme.visualPrimitives.focusRingWidth} solid ${theme.colors.lavenderDeep}`,
          outlineOffset: theme.visualPrimitives.focusRingOffset,
        },
      })}
    >
      <Table size="small" sx={{ minWidth: compact ? 520 : 620, tableLayout: 'auto' }}>
        <Box
          component="caption"
          sx={theme => ({
            captionSide: 'top',
            textAlign: 'left',
            color: theme.colors.inkTertiary,
            fontSize: theme.visualPrimitives.eyebrowSize,
            fontWeight: 500,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            paddingBottom: 2,
          })}
        >
          {caption}
        </Box>
        <TableHead>
          <TableRow>
            {columns.map(column => (
              <TableCell
                key={column}
                sx={theme => ({
                  borderBottom: `1px solid ${theme.visualPrimitives.border}`,
                  color: theme.colors.inkTertiary,
                  fontSize: theme.visualPrimitives.briefTableHeaderSize,
                  fontWeight: 500,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  paddingX: 0,
                  paddingY: 1.5,
                  paddingRight: 2,
                  verticalAlign: 'bottom',
                  whiteSpace: 'nowrap',
                })}
              >
                {humanize(column)}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, rowIndex) => (
            <TableRow key={rowIndex}>
              {columns.map((column, columnIndex) => (
                <TableCell
                  key={column}
                  sx={theme => ({
                    borderBottom: `1px solid ${theme.visualPrimitives.borderLight}`,
                    color: columnIndex === 0 ? theme.colors.inkPrimary : theme.colors.inkSecondary,
                    fontSize: compact ? '14px' : theme.visualPrimitives.briefTableCellSize,
                    lineHeight: 1.55,
                    paddingX: 0,
                    paddingY: 2,
                    paddingRight: 2,
                    verticalAlign: 'top',
                    ...(columnIndex > 0 && {
                      fontVariantNumeric: 'tabular-nums',
                    }),
                  })}
                >
                  {column === 'recommended' ? (
                    <AdvisoryRecommendationValue value={row[column]} />
                  ) : (
                    <AdvisoryEvidenceValue>{row[column]}</AdvisoryEvidenceValue>
                  )}
                  {citeTableSources && column === 'claim' ? (
                    <AdvisoryTableSourceCitations source={row.source} />
                  ) : null}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function BriefList({
  title,
  values,
  compact,
}: {
  title: string;
  values: readonly string[];
  compact: boolean;
}) {
  const { t } = useUntypedTranslation();
  return (
    <Box>
      <Typography component={compact ? 'h5' : 'h4'} sx={[eyebrowSx, { marginBottom: 1.5 }]}>
        {title}
      </Typography>
      {values.length === 0 ? (
        <Typography sx={theme => ({ color: theme.colors.inkTertiary, fontStyle: 'italic' })}>
          {t('advisory:brief.knownEmpty')}
        </Typography>
      ) : (
        <Box component="ol" sx={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {values.map((value, index) => (
            <Box
              component="li"
              key={`${value}-${index}`}
              sx={theme => ({
                display: 'grid',
                gridTemplateColumns: '44px minmax(0, 1fr)',
                gap: 3,
                paddingY: 2,
                borderBottom: `1px solid ${theme.visualPrimitives.borderLight}`,
                alignItems: 'baseline',
              })}
            >
              <Typography
                component="span"
                sx={[monoSx, theme => ({ color: theme.colors.inkTertiary, fontSize: 12 })]}
              >
                {String(index + 1).padStart(2, '0')}
              </Typography>
              {value.trim().length > 0 ? (
                <Typography>
                  <AdvisoryEvidenceText>{value}</AdvisoryEvidenceText>
                </Typography>
              ) : (
                <UnavailableValue />
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

interface SchemaSectionProps {
  section: AnyCompositeSectionV01;
  compact: boolean;
  prominentFields?: readonly string[];
  calloutFields?: readonly string[];
}

function SchemaSection({
  section,
  compact,
  prominentFields = [],
  calloutFields = [],
}: SchemaSectionProps) {
  const schema = COMPONENT_SCHEMAS[section.componentId];
  const data = section.data as Record<string, unknown>;

  return (
    <Box sx={{ display: 'grid', gap: compact ? 3 : 4 }}>
      {section.fields.map(field => {
        const attribute = schema.attributes.find(candidate => candidate.name === field);
        if (!attribute) {
          return null;
        }
        const value = data[field];
        const title = humanize(field);

        if (attribute.type === 'rows') {
          return (
            <BriefTable
              key={field}
              caption={title}
              columns={(attribute.columns ?? []).filter(
                column => section.componentId !== 'evidence-table' || column !== 'source'
              )}
              citeTableSources={section.componentId === 'evidence-table'}
              rows={(Array.isArray(value) ? value : []) as DisplayRow[]}
              compact={compact}
            />
          );
        }
        if (attribute.type === 'list') {
          return (
            <BriefList
              key={field}
              title={title}
              values={(Array.isArray(value) ? value : []) as string[]}
              compact={compact}
            />
          );
        }
        if (attribute.type === 'markdown') {
          const markdown = typeof value === 'string' ? value : '';
          return markdown.trim().length === 0 ? (
            <UnavailableValue key={field} />
          ) : (
            <Box
              key={field}
              className="advisory-brief-markdown"
              sx={theme => ({
                color: theme.colors.inkSecondary,
                fontSize: theme.visualPrimitives.briefBodySize,
                lineHeight: theme.visualPrimitives.briefBodyLineHeight,
                maxWidth: '40em',
                '& p': { marginTop: 0, marginBottom: '1.4em' },
                '& strong': { color: theme.colors.inkPrimary, fontWeight: 500 },
                '& h1, & h2, & h3': {
                  color: theme.colors.inkPrimary,
                  fontSize: theme.visualPrimitives.briefSummaryValueSize,
                  fontWeight: 500,
                },
              })}
            >
              <AdvisoryEvidenceMarkdown>{markdown}</AdvisoryEvidenceMarkdown>
            </Box>
          );
        }

        const stringValue = typeof value === 'string' ? value : '';
        if (calloutFields.includes(field)) {
          return (
            <Box
              key={field}
              sx={theme => ({
                borderLeft: `2px solid ${theme.colors.lavender}`,
                padding: theme.visualPrimitives.briefCalloutPadding,
                maxWidth: 640,
              })}
            >
              <Typography component={compact ? 'h5' : 'h4'} sx={[eyebrowSx, { marginBottom: 1.5 }]}>
                {title}
              </Typography>
              <DisplayValue value={stringValue} />
            </Box>
          );
        }
        return (
          <Box key={field}>
            {!prominentFields.includes(field) ? (
              <Typography
                component={compact ? 'h5' : 'h4'}
                sx={[eyebrowSx, { marginBottom: 1.25 }]}
              >
                {title}
              </Typography>
            ) : null}
            <DisplayValue value={stringValue} prominent={prominentFields.includes(field)} />
          </Box>
        );
      })}
    </Box>
  );
}

function GenericSection({
  section,
  compact,
}: {
  section: LiveCompositeSectionV01;
  compact: boolean;
}) {
  return (
    <Box data-testid="advisory-generic-component" sx={{ display: 'grid', gap: compact ? 3 : 4 }}>
      {section.fields.map(field => {
        const value = section.data[field];
        if (typeof value === 'string') {
          return (
            <Box key={field}>
              <Typography component={compact ? 'h5' : 'h4'} sx={[eyebrowSx, { marginBottom: 1.5 }]}>
                {humanize(field)}
              </Typography>
              <DisplayValue value={value} />
            </Box>
          );
        }
        if (Array.isArray(value) && value.every(item => typeof item === 'string')) {
          return <BriefList key={field} title={humanize(field)} values={value} compact={compact} />;
        }
        const rows = Array.isArray(value)
          ? value.filter(
              (item): item is Record<string, string | number | null> =>
                item !== null && typeof item === 'object' && !Array.isArray(item)
            )
          : [];
        const columns = rows.reduce<string[]>((all, row) => {
          Object.keys(row).forEach(column => {
            if (!all.includes(column)) all.push(column);
          });
          return all;
        }, []);
        const displayRows = rows.map(row =>
          columns.reduce<DisplayRow>((mapped, column) => {
            const cell = row[column];
            mapped[column] = cell === null || cell === undefined ? '' : String(cell);
            return mapped;
          }, {})
        );
        return (
          <BriefTable
            key={field}
            caption={humanize(field)}
            columns={columns}
            rows={displayRows}
            compact={compact}
          />
        );
      })}
    </Box>
  );
}

interface ComponentRendererProps {
  section: AnyCompositeSectionV01;
  compact: boolean;
}

function DecisionRenderer(props: ComponentRendererProps) {
  return (
    <SchemaSection {...props} prominentFields={['decision']} calloutFields={['smallest_test']} />
  );
}

function ActionPriorityRenderer({ section, compact }: ComponentRendererProps) {
  const rows = (section.data as { rows?: DisplayRow[] }).rows ?? [];
  const { t } = useUntypedTranslation();
  return rows.length === 0 ? (
    <Typography sx={theme => ({ color: theme.colors.inkTertiary, fontStyle: 'italic' })}>
      {t('advisory:brief.knownEmpty')}
    </Typography>
  ) : (
    <Box component="ol" sx={{ listStyle: 'none', margin: 0, padding: 0 }}>
      {rows.map((row, index) => (
        <Box
          component="li"
          key={index}
          sx={theme => ({
            display: 'grid',
            gridTemplateColumns: { xs: '36px minmax(0, 1fr)', sm: '44px minmax(0, 1fr) auto' },
            gap: { xs: 1.5, sm: 3 },
            paddingY: compact ? 1.5 : 2.5,
            borderBottom: `1px solid ${theme.visualPrimitives.borderLight}`,
            alignItems: 'baseline',
          })}
        >
          <Typography sx={[monoSx, theme => ({ color: theme.colors.inkTertiary, fontSize: 12 })]}>
            {(row.priority || String(index + 1)).padStart(2, '0')}
          </Typography>
          <Box>
            {(row.action ?? '').trim() ? (
              <Typography>
                <AdvisoryEvidenceText>{row.action}</AdvisoryEvidenceText>
              </Typography>
            ) : (
              <UnavailableValue />
            )}
            <Typography
              sx={theme => ({ color: theme.colors.inkTertiary, fontSize: 13, marginTop: 0.5 })}
            >
              <AdvisoryEvidenceText>{row.reinforces}</AdvisoryEvidenceText>
            </Typography>
          </Box>
          <Typography
            sx={theme => ({
              color: theme.colors.inkTertiary,
              fontSize: 13,
              gridColumn: { xs: '2', sm: 'auto' },
              whiteSpace: { sm: 'nowrap' },
            })}
          >
            <AdvisoryEvidenceText>{row.owner_or_horizon}</AdvisoryEvidenceText>
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function MetricStripRenderer({ section }: ComponentRendererProps) {
  const { t } = useUntypedTranslation();
  const rows = (section.data as { rows?: DisplayRow[] }).rows ?? [];
  if (rows.length === 0) {
    return (
      <Typography sx={theme => ({ color: theme.colors.inkTertiary, fontStyle: 'italic' })}>
        {t('advisory:brief.knownEmpty')}
      </Typography>
    );
  }
  return (
    <Box
      sx={theme => ({
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, minmax(0, 1fr))',
          md: 'repeat(3, minmax(0, 1fr))',
        },
        borderTop: `1px solid ${theme.visualPrimitives.border}`,
      })}
    >
      {rows.map((row, index) => (
        <Box key={index} sx={{ paddingTop: 3, paddingRight: 4, minWidth: 0 }}>
          <Typography sx={[eyebrowSx, { marginBottom: 1 }]}>{row.label}</Typography>
          {(row.value ?? '').trim() ? (
            <Typography
              sx={[
                monoSx,
                theme => ({ color: theme.colors.inkPrimary, fontSize: 22, fontWeight: 500 }),
              ]}
            >
              <AdvisoryEvidenceText>{row.value}</AdvisoryEvidenceText>
            </Typography>
          ) : (
            <UnavailableValue />
          )}
          <Typography
            sx={theme => ({ color: theme.colors.inkTertiary, fontSize: 13, marginTop: 0.5 })}
          >
            {[row.unit, row.period].filter(Boolean).join(' · ')}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

const schemaRenderer = (props: ComponentRendererProps) => <SchemaSection {...props} />;
const counterPositionRenderer = (props: ComponentRendererProps) => (
  <SchemaSection {...props} calloutFields={['smallest_test']} />
);
const sensitivityRenderer = (props: ComponentRendererProps) => (
  <SchemaSection {...props} calloutFields={['breakpoint']} />
);
const durabilityRenderer = (props: ComponentRendererProps) => (
  <SchemaSection {...props} prominentFields={['advantage']} />
);

export const ADVISORY_COMPONENT_RENDERERS: Readonly<
  Record<AdvisoryComponentId, React.ComponentType<ComponentRendererProps>>
> = {
  decision: DecisionRenderer,
  'action-priority': ActionPriorityRenderer,
  'business-model-canvas': BusinessModelCanvasRenderer,
  'comparison-matrix': schemaRenderer,
  'counter-position': counterPositionRenderer,
  'durability-factors': durabilityRenderer,
  'evidence-table': schemaRenderer,
  'financial-forecast': FinancialForecastRenderer,
  'market-sizing': schemaRenderer,
  'metric-strip': MetricStripRenderer,
  narrative: schemaRenderer,
  'options-matrix': schemaRenderer,
  'people-grid': schemaRenderer,
  'portfolio-matrix': schemaRenderer,
  'risk-matrix': schemaRenderer,
  'roadmap-timeline': RoadmapTimelineRenderer,
  'scenario-matrix': ScenarioMatrixRenderer,
  'sensitivity-plot': sensitivityRenderer,
  'system-map': SystemMapRenderer,
  'value-bridge': schemaRenderer,
};

/** Kept closed over the Competitor Analysis contract inventory. */
export const COMPETITOR_COMPONENT_RENDERERS: Readonly<
  Record<CompetitorAnalysisComponentId, React.ComponentType<ComponentRendererProps>>
> = {
  decision: ADVISORY_COMPONENT_RENDERERS.decision,
  'action-priority': ADVISORY_COMPONENT_RENDERERS['action-priority'],
  'counter-position': ADVISORY_COMPONENT_RENDERERS['counter-position'],
  'options-matrix': ADVISORY_COMPONENT_RENDERERS['options-matrix'],
  'sensitivity-plot': ADVISORY_COMPONENT_RENDERERS['sensitivity-plot'],
  'market-sizing': ADVISORY_COMPONENT_RENDERERS['market-sizing'],
  'portfolio-matrix': ADVISORY_COMPONENT_RENDERERS['portfolio-matrix'],
  'durability-factors': ADVISORY_COMPONENT_RENDERERS['durability-factors'],
  'risk-matrix': ADVISORY_COMPONENT_RENDERERS['risk-matrix'],
  'people-grid': ADVISORY_COMPONENT_RENDERERS['people-grid'],
  'evidence-table': ADVISORY_COMPONENT_RENDERERS['evidence-table'],
  'metric-strip': ADVISORY_COMPONENT_RENDERERS['metric-strip'],
  narrative: ADVISORY_COMPONENT_RENDERERS.narrative,
};

interface LayoutProps {
  children: React.ReactNode;
}

const HeroGrid = ({ children }: LayoutProps) => (
  <Box sx={{ display: 'grid', gap: 5 }}>{children}</Box>
);
const SplitAnalysis = ({ children }: LayoutProps) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
      gap: { xs: 5, md: 7 },
      // Actual tables, including generic components, always get the full reading width.
      '&:has(table)': { gridTemplateColumns: 'minmax(0, 1fr)' },
      '& > :last-child:nth-of-type(3)': { gridColumn: { md: '1 / -1' } },
    }}
  >
    {children}
  </Box>
);
const NarrativeTable = ({ children }: LayoutProps) => (
  <Box sx={{ display: 'grid', gap: 5 }}>{children}</Box>
);
const FullVisual = ({ children }: LayoutProps) => (
  <Box sx={{ display: 'grid', gap: 5 }}>{children}</Box>
);
const Continuation = ({ children }: LayoutProps) => (
  <Box sx={{ display: 'grid', gap: 5 }}>{children}</Box>
);

export const COMPETITOR_LAYOUT_RENDERERS: Readonly<
  Record<LayoutPreset, React.ComponentType<LayoutProps>>
> = {
  'hero-grid': HeroGrid,
  'split-analysis': SplitAnalysis,
  'narrative-table': NarrativeTable,
  'full-visual': FullVisual,
  continuation: Continuation,
};

function UnavailableSection({ title, reason }: { title: string; reason: string }) {
  const { t } = useUntypedTranslation();
  return (
    <Box
      role="note"
      sx={theme => ({
        borderLeft: `2px solid ${theme.colors.vermilion}`,
        padding: theme.visualPrimitives.briefCalloutPadding,
        maxWidth: 640,
      })}
    >
      <Typography component="h3" sx={[eyebrowSx, theme => ({ color: theme.colors.vermilion })]}>
        {title}
      </Typography>
      <Typography sx={theme => ({ color: theme.colors.inkSecondary, marginTop: 1 })}>
        {t('advisory:brief.sectionUnavailable', { reason })}
      </Typography>
    </Box>
  );
}

function InvalidAnswer({ issues }: { issues: readonly ValidationIssue[] }) {
  const { t } = useUntypedTranslation();
  return (
    <Box
      role="alert"
      data-testid="advisory-brief-invalid-answer"
      sx={theme => ({
        borderLeft: `2px solid ${theme.colors.vermilion}`,
        padding: theme.visualPrimitives.briefCalloutPadding,
        maxWidth: 680,
      })}
    >
      <Typography component="h3" sx={[eyebrowSx, theme => ({ color: theme.colors.vermilion })]}>
        {t('advisory:brief.invalidAnswer')}
      </Typography>
      <Box component="ul" sx={{ marginBottom: 0, paddingLeft: 2.5 }}>
        {issues.map((issue, index) => (
          <Typography
            component="li"
            key={`${issue.path}-${issue.code}-${index}`}
            sx={{ marginTop: 1 }}
          >
            {issue.message}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}

function AnswerTextFallback({ answerText }: { answerText: string }) {
  const { t } = useUntypedTranslation();
  return (
    <Box
      data-testid="advisory-brief-answer-text-fallback"
      sx={theme => ({
        borderLeft: `2px solid ${theme.colors.lavender}`,
        padding: theme.visualPrimitives.briefCalloutPadding,
        maxWidth: 720,
      })}
    >
      <Typography component="h3" sx={[eyebrowSx, { marginBottom: 2 }]}>
        {t('advisory:brief.answerTextFallback')}
      </Typography>
      <Box
        className="advisory-brief-markdown"
        sx={theme => ({
          color: theme.colors.inkSecondary,
          fontSize: theme.visualPrimitives.briefBodySize,
          lineHeight: theme.visualPrimitives.briefBodyLineHeight,
          '& p': { marginTop: 0, marginBottom: '1.4em' },
          '& p:last-child': { marginBottom: 0 },
          '& strong': { color: theme.colors.inkPrimary, fontWeight: 500 },
        })}
      >
        <AdvisoryEvidenceMarkdown>{answerText}</AdvisoryEvidenceMarkdown>
      </Box>
    </Box>
  );
}

function BriefSectionSlot({
  answer,
  componentId,
  compact,
}: {
  answer: LiveCompositeAnswerV01;
  componentId: string;
  compact: boolean;
}) {
  const { t } = useUntypedTranslation();
  const section = answer.sections.find(candidate => candidate.componentId === componentId);
  if (!section) {
    const omitted = answer.omitted.find(candidate => candidate.componentId === componentId);
    return (
      <UnavailableSection
        title={omitted?.title ?? componentId}
        reason={omitted?.reason ?? t('advisory:brief.missingPlannedSection')}
      />
    );
  }

  const Renderer = Object.prototype.hasOwnProperty.call(
    ADVISORY_COMPONENT_RENDERERS,
    section.componentId
  )
    ? ADVISORY_COMPONENT_RENDERERS[section.componentId as AdvisoryComponentId]
    : null;

  return (
    <Box data-component-id={section.componentId} sx={{ minWidth: 0 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          flexWrap: 'wrap',
          gap: 1.5,
          marginBottom: compact ? 2 : 3,
        }}
      >
        <Typography component={compact ? 'h4' : 'h3'} sx={[eyebrowSx]}>
          {section.title}
        </Typography>
        {section.filledFrom === 'computation' ? (
          <Typography
            component="span"
            data-filled-from="computation"
            sx={theme => ({
              color: theme.colors.lavenderDeep,
              fontSize: 11,
              fontWeight: 500,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
            })}
          >
            {t('advisory:brief.computed')}
          </Typography>
        ) : null}
      </Box>
      {Renderer ? (
        <Renderer section={section as AnyCompositeSectionV01} compact={compact} />
      ) : (
        <GenericSection section={section} compact={compact} />
      )}
    </Box>
  );
}

function BriefSlide({
  answer,
  slide,
  compact,
}: {
  answer: LiveCompositeAnswerV01;
  slide: LiveCompositeSlideV01;
  compact: boolean;
}) {
  const { t } = useUntypedTranslation();
  const Layout = COMPETITOR_LAYOUT_RENDERERS[slide.layout];
  if (!Layout) {
    return <UnavailableSection title={slide.layout} reason={t('advisory:brief.missingLayout')} />;
  }
  return (
    <Box data-layout={slide.layout} data-testid="advisory-brief-slide">
      <Layout>
        {slide.sections.map(componentId => (
          <BriefSectionSlot
            key={componentId}
            answer={answer}
            componentId={componentId}
            compact={compact}
          />
        ))}
      </Layout>
    </Box>
  );
}

function QuestionPlan({
  state,
  plan,
  compact,
}: {
  state: QuestionState;
  plan: PlanKind;
  compact: boolean;
}) {
  if (state.fallbackText) {
    return <AnswerTextFallback answerText={state.fallbackText} />;
  }
  if (!state.answer) {
    return <InvalidAnswer issues={state.issues ?? []} />;
  }
  return (
    <Box sx={{ display: 'grid', gap: compact ? 4 : 0 }}>
      {state.answer.slidePlans[plan].map((slide, index) => (
        <BriefSlide
          key={`${slide.layout}-${index}`}
          answer={state.answer}
          slide={slide}
          compact={compact}
        />
      ))}
    </Box>
  );
}

export function AdvisoryBriefDocument({
  model,
  loadSourcePassage,
}: {
  model: AdvisoryBriefDocumentInputV01;
  loadSourcePassage?: AdvisorySourcePassageLoader;
}) {
  const { t } = useUntypedTranslation();
  // Chapters are addressed by their stored answer key throughout: it is what audit
  // findings name, and unlike questionId it stays distinct across two answers to
  // the same key question.
  const [panel, setPanel] = useState<{ view: 'sources' | 'audit'; answerKey?: string } | null>(
    null
  );
  const answerKeyOf = (input: AdvisoryBriefQuestionInputV01) => input.answerKey || input.questionId;
  const sourceAnswerKey = panel?.answerKey;
  const quoteTriggerRef = useRef<HTMLElement | null>(null);
  const chapterHeadingsRef = useRef(new Map<string, HTMLHeadingElement>());
  const chapterMarkersRef = useRef(new Map<string, HTMLParagraphElement>());
  const pendingChapterRef = useRef<string | null>(null);
  useLayoutEffect(() => {
    if (panel === null && quoteTriggerRef.current) {
      quoteTriggerRef.current.focus({ preventScroll: true });
      quoteTriggerRef.current = null;
    }
  }, [panel]);
  useEffect(() => {
    if (panel !== null || pendingChapterRef.current === null) return;
    const heading = chapterHeadingsRef.current.get(pendingChapterRef.current);
    const marker = chapterMarkersRef.current.get(pendingChapterRef.current);
    pendingChapterRef.current = null;
    // Keep focus in the sidebar during navigation, then move it to the last
    // visited chapter after the drawer closes and restores its trigger.
    heading?.focus({ preventScroll: true });
    marker?.scrollIntoView({ behavior: 'auto', block: 'start' });
  }, [panel]);
  const [quoteReferenceKey, setQuoteReferenceKey] = useState<string | null>(null);
  const answerEvidence = useMemo<AdvisoryAnswerEvidenceEntry[]>(
    () =>
      model.answers.map(input => ({
        input,
        evidence:
          input.citations == null
            ? null
            : mapAdvisoryAnswerCitations(input.citations, input.sourceCompleteness),
      })),
    [model.answers]
  );
  const quoteReferences = useMemo(() => {
    let number = 0;
    const labelledQuotes: AdvisoryQuoteReference[] = [];
    return Object.fromEntries(
      answerEvidence.map(({ input, evidence }) => {
        const references: AdvisoryQuoteReference[] = evidence?.ok
          ? evidence.value.sources.flatMap(source =>
              (source.quotes ?? []).map((quote, quoteIndex) => {
                const existing = labelledQuotes.find(
                  reference =>
                    sameAdvisoryPassage(reference.source, source) &&
                    reference.quote.text === quote.text &&
                    reference.quote.status === quote.status &&
                    reference.quote.segmentIndex === quote.segmentIndex
                );
                const reference: AdvisoryQuoteReference = {
                  key: JSON.stringify([
                    answerKeyOf(input),
                    source.key,
                    quote.text,
                    quote.status,
                    quote.segmentIndex,
                  ]),
                  label: existing?.label ?? `S${++number}`,
                  source,
                  quote,
                  quoteIndex,
                };
                labelledQuotes.push(reference);
                return reference;
              })
            )
          : [];
        return [answerKeyOf(input), references];
      })
    );
  }, [answerEvidence]);
  const tableReferences = useMemo(
    () => buildAdvisoryTableSourceReferences(answerEvidence, quoteReferences),
    [answerEvidence, quoteReferences]
  );
  const chapterEvidence = useMemo(
    () =>
      Object.fromEntries(
        answerEvidence.map(({ input, evidence }) => [
          answerKeyOf(input),
          separateAdvisorySourceEntries(
            evidence,
            quoteReferences[answerKeyOf(input)] ?? [],
            [answerKeyOf(input)],
            tableReferences[answerKeyOf(input)]
          ),
        ])
      ),
    [answerEvidence, quoteReferences, tableReferences]
  );
  const reportEvidence = useMemo(
    () => collectAdvisoryReportEvidence(answerEvidence, quoteReferences, tableReferences),
    [answerEvidence, quoteReferences, tableReferences]
  );
  const openSourceIndex = (answerKey?: string) => {
    setQuoteReferenceKey(null);
    setPanel({ view: 'sources', answerKey });
  };
  const openAudit = (answerKey?: string) => {
    setQuoteReferenceKey(null);
    setPanel({ view: 'audit', answerKey });
  };
  const inlineEvidence = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(quoteReferences).map(([answerKey, references]) => [
          answerKey,
          {
            references,
            tableReferences: tableReferences[answerKey],
            onOpenTableSource: (reference: AdvisoryTableSourceReference, origin: HTMLElement) => {
              quoteTriggerRef.current = origin;
              setQuoteReferenceKey(reference.key);
              setPanel({ view: 'sources', answerKey });
            },
            onOpen: (reference: AdvisoryQuoteReference, origin: HTMLElement) => {
              quoteTriggerRef.current = origin;
              setQuoteReferenceKey(reference.key);
              setPanel({ view: 'sources', answerKey });
            },
          },
        ])
      ),
    [quoteReferences, tableReferences]
  );
  const states: QuestionState[] = model.answers.map(input => {
    const isEmptyRendering =
      input.rendering !== null &&
      typeof input.rendering === 'object' &&
      !Array.isArray(input.rendering) &&
      Object.keys(input.rendering).length === 0;
    if (isEmptyRendering && input.answerText?.trim()) {
      return {
        input,
        answer: null,
        issues: null,
        fallbackText: input.answerText,
      };
    }
    const result = model.allowedQuestionIds
      ? validateLiveRenderingEnvelopeV01(input.rendering, {
          engagementId: model.engagementId,
          questionId: input.questionId,
          allowedQuestionIds: model.allowedQuestionIds,
        })
      : validateCompositeAnswerV01(input.rendering, {
          engagementId: model.engagementId,
          questionId: input.questionId,
        });
    return result.ok
      ? { input, answer: result.value, issues: null, fallbackText: null }
      : { input, answer: null, issues: result.issues, fallbackText: null };
  });
  const selectedSourceState = sourceAnswerKey
    ? states.find(state => answerKeyOf(state.input) === sourceAnswerKey) ?? null
    : null;
  const selectedEvidence =
    selectedSourceState && panel?.view !== 'audit'
      ? chapterEvidence[answerKeyOf(selectedSourceState.input)]
      : reportEvidence;
  // A passage the producer left unidentified cannot be recognised by comparison,
  // so the register that carried it names its own chapters.
  const sourceUses = useCallback(
    (source: AdvisorySourceReferenceV01) =>
      answerEvidence.flatMap(({ input, evidence }, index) => {
        const answerKey = input.answerKey || input.questionId;
        return source.answerKeys?.includes(answerKey) ||
          (evidence?.ok && evidence.value.sources.some(other => sameAdvisoryPassage(source, other)))
          ? [{ answerKey, label: input.question, chapter: index + 1 }]
          : [];
      }),
    [answerEvidence]
  );
  const selectedQuote = selectedEvidence.references.find(
    reference => reference.key === quoteReferenceKey
  );
  const selectedTableSource = selectedEvidence.tableReferences.find(
    reference => reference.key === quoteReferenceKey
  );
  const canOpenSources = (state: QuestionState): boolean =>
    state.input.citations != null ||
    findingsForAnswer(model.audit, answerKeyOf(state.input)).length > 0 ||
    (model.researchContextStatus !== undefined && model.researchContextStatus !== 'loading');
  const answerLabels = Object.fromEntries(
    model.answers.map(answer => [answerKeyOf(answer), answer.question])
  );
  const answerChapters = Object.fromEntries(
    model.answers.map((answer, index) => [answerKeyOf(answer), index + 1])
  );
  const auditNeedsAttention =
    model.audit && 'report' in model.audit && model.audit.report.findings.length > 0;
  const navigateToAnswer = (answerKey: string) => {
    if (!model.answers.some(input => answerKeyOf(input) === answerKey)) return;
    pendingChapterRef.current = answerKey;
    quoteTriggerRef.current = null;
    chapterMarkersRef.current.get(answerKey)?.scrollIntoView({ behavior: 'auto', block: 'start' });
  };

  return (
    <Box
      data-testid="advisory-brief-document"
      sx={theme => ({
        position: 'relative',
        overflowX: 'hidden',
        backgroundColor: theme.palette.background.default,
        color: theme.colors.inkPrimary,
        fontSize: theme.visualPrimitives.briefBodySize,
        lineHeight: theme.visualPrimitives.briefBodyLineHeight,
      })}
    >
      <Box
        component="section"
        sx={[briefSpread, { minHeight: 'auto', paddingBottom: { xs: 4, md: 5 } }]}
      >
        <Box sx={[briefColumn]}>
          <BriefReveal>
            <Typography
              component="h1"
              sx={theme => ({
                color: theme.colors.inkPrimary,
                fontSize: theme.visualPrimitives.briefCoverTitleSize,
                fontWeight: 500,
                letterSpacing: theme.visualPrimitives.briefCoverTitleTracking,
                lineHeight: theme.visualPrimitives.briefCoverTitleLineHeight,
                maxWidth: '14em',
              })}
            >
              {model.title}
            </Typography>
            <Typography
              sx={theme => ({
                color: theme.colors.inkSecondary,
                fontSize: theme.visualPrimitives.briefCoverSubtitleSize,
                lineHeight: 1.65,
                marginTop: 3.5,
                maxWidth: '46em',
              })}
            >
              {model.subtitle}
            </Typography>

            {states.some(canOpenSources) ||
            (model.audit && model.audit.status !== 'unavailable') ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, marginTop: 3 }}>
                {states.some(canOpenSources) ? (
                  <Button
                    size="small"
                    data-testid="advisory-report-sources-trigger"
                    onClick={() => openSourceIndex()}
                    sx={briefEvidenceLinkSx}
                  >
                    {t('advisory:brief.sources.open')}
                  </Button>
                ) : null}
                {model.audit && model.audit.status !== 'unavailable' ? (
                  <Tooltip
                    title={auditNeedsAttention ? t('advisory:brief.audit.attention') : ''}
                    describeChild
                  >
                    <Button
                      size="small"
                      data-testid="advisory-report-audit-trigger"
                      onClick={() => openAudit()}
                      sx={[
                        briefEvidenceLinkSx,
                        theme => ({
                          color: auditNeedsAttention ? theme.colors.vermilion : undefined,
                        }),
                      ]}
                      aria-label={
                        auditNeedsAttention
                          ? `${t('advisory:brief.audit.title')}: ${t(
                              'advisory:brief.audit.attention'
                            )}`
                          : undefined
                      }
                    >
                      {t('advisory:brief.audit.title')}
                    </Button>
                  </Tooltip>
                ) : null}
              </Box>
            ) : null}
          </BriefReveal>
        </Box>
      </Box>

      <Box
        component="section"
        sx={[
          briefSpread,
          theme => ({
            borderTop: `1px solid ${theme.visualPrimitives.border}`,
            justifyContent: 'flex-start',
            paddingTop: { xs: 4, md: 5 },
          }),
        ]}
      >
        <Box sx={[briefColumn]}>
          <Typography sx={[eyebrowSx, { marginBottom: 1.5 }]}>
            {t('advisory:brief.summary.eyebrow')}
          </Typography>
          <Typography
            component="h2"
            sx={theme => ({
              fontSize: theme.visualPrimitives.briefChapterTitleSize,
              fontWeight: 500,
              letterSpacing: theme.visualPrimitives.briefChapterTitleTracking,
              lineHeight: theme.visualPrimitives.briefChapterTitleLineHeight,
              marginBottom: 5,
            })}
          >
            {t('advisory:brief.summary.title')}
          </Typography>
          <Box>
            {states.map((state, index) => (
              <Box
                key={answerKeyOf(state.input)}
                data-summary-question-id={state.input.questionId}
                sx={theme => ({
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '170px minmax(0, 1fr)' },
                  gap: { xs: 1.5, md: 4 },
                  paddingY: 3.25,
                  borderBottom: `1px solid ${theme.visualPrimitives.borderLight}`,
                  alignItems: 'start',
                })}
              >
                <Box>
                  <Typography
                    sx={[monoSx, theme => ({ color: theme.colors.inkTertiary, fontSize: 12 })]}
                  >
                    {String(index + 1).padStart(2, '0')}
                  </Typography>
                  <Typography
                    component="h3"
                    sx={theme => ({
                      color: theme.colors.inkSecondary,
                      fontSize: 15,
                      marginTop: 1,
                    })}
                  >
                    {state.input.question}
                  </Typography>
                  <Box sx={{ marginTop: 1.25 }}>
                    <AnswerStatusBadge status={state.input.status} />
                  </Box>
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <AdvisoryInlineEvidenceProvider value={inlineEvidence[answerKeyOf(state.input)]}>
                    <QuestionPlan state={state} plan="short" compact />
                  </AdvisoryInlineEvidenceProvider>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {states.map((state, questionIndex) => {
        const chapterNeedsAttention = chapterNeedsAuditAttention(
          model.audit,
          answerKeyOf(state.input),
          quoteReferences[answerKeyOf(state.input)] ?? []
        );
        const slides = state.answer?.slidePlans.long ?? [null];
        return (
          <Box
            component="article"
            key={answerKeyOf(state.input)}
            data-question-id={state.input.questionId}
          >
            {slides.map((slide, slideIndex) => (
              <Box
                component="section"
                key={slide ? `${slide.layout}-${slideIndex}` : 'invalid'}
                sx={[briefSpread]}
              >
                <Box sx={[briefColumn]}>
                  <BriefReveal>
                    {slideIndex === 0 ? (
                      <Box sx={theme => ({ marginBottom: theme.visualPrimitives.briefSectionGap })}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 1.5,
                            marginBottom: 2.5,
                          }}
                        >
                          <Typography
                            component="p"
                            data-testid={`advisory-chapter-marker-${state.input.questionId}`}
                            ref={(marker: HTMLParagraphElement | null) => {
                              if (marker)
                                chapterMarkersRef.current.set(answerKeyOf(state.input), marker);
                              else chapterMarkersRef.current.delete(answerKeyOf(state.input));
                            }}
                            sx={[
                              monoSx,
                              theme => ({
                                color: theme.colors.inkTertiary,
                                fontSize: 13,
                                scrollMarginTop: '2rem',
                              }),
                            ]}
                          >
                            {t('advisory:brief.chapterMarker', {
                              position: String(questionIndex + 1).padStart(2, '0'),
                            })}
                          </Typography>
                          {state.input.status !== 'answered' ? (
                            <AnswerStatusBadge status={state.input.status} />
                          ) : null}
                        </Box>
                        <Typography
                          component="h2"
                          ref={(heading: HTMLHeadingElement | null) => {
                            if (heading)
                              chapterHeadingsRef.current.set(answerKeyOf(state.input), heading);
                            else chapterHeadingsRef.current.delete(answerKeyOf(state.input));
                          }}
                          tabIndex={-1}
                          sx={theme => ({
                            color: theme.colors.inkPrimary,
                            fontSize: theme.visualPrimitives.briefChapterTitleSize,
                            fontWeight: 500,
                            letterSpacing: theme.visualPrimitives.briefChapterTitleTracking,
                            lineHeight: theme.visualPrimitives.briefChapterTitleLineHeight,
                            maxWidth: '16em',
                          })}
                        >
                          {state.input.question}
                        </Typography>
                        {(model.audit && model.audit.status !== 'unavailable') ||
                        canOpenSources(state) ? (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, marginTop: 2.5 }}>
                            {model.audit && model.audit.status !== 'unavailable' ? (
                              <Button
                                size="small"
                                data-testid={`advisory-audit-trigger-${state.input.questionId}`}
                                onClick={() => openAudit(answerKeyOf(state.input))}
                                sx={[
                                  briefEvidenceLinkSx,
                                  theme => ({
                                    color: chapterNeedsAttention
                                      ? theme.colors.vermilion
                                      : undefined,
                                  }),
                                ]}
                              >
                                {t('advisory:brief.audit.reviewChapter')}
                              </Button>
                            ) : null}
                            {canOpenSources(state) ? (
                              <Button
                                data-testid={`advisory-sources-trigger-${state.input.questionId}`}
                                onClick={() => openSourceIndex(answerKeyOf(state.input))}
                                size="small"
                                sx={briefEvidenceLinkSx}
                              >
                                {t('advisory:brief.sources.open')}
                              </Button>
                            ) : null}
                          </Box>
                        ) : null}
                        {state.input.caveat ? (
                          <Typography
                            role="note"
                            sx={theme => ({
                              color: theme.colors.vermilion,
                              marginTop: 2,
                              maxWidth: '48em',
                            })}
                          >
                            {state.input.caveat}
                          </Typography>
                        ) : null}
                      </Box>
                    ) : null}
                    <AdvisoryInlineEvidenceProvider
                      value={inlineEvidence[answerKeyOf(state.input)]}
                    >
                      {state.fallbackText ? (
                        <AnswerTextFallback answerText={state.fallbackText} />
                      ) : slide && state.answer ? (
                        <BriefSlide answer={state.answer} slide={slide} compact={false} />
                      ) : (
                        <InvalidAnswer issues={state.issues ?? []} />
                      )}
                    </AdvisoryInlineEvidenceProvider>
                  </BriefReveal>
                </Box>
              </Box>
            ))}
          </Box>
        );
      })}

      {panel ? (
        <AdvisorySourcesDrawer
          key={selectedQuote?.key ?? selectedTableSource?.key ?? JSON.stringify(panel)}
          view={panel.view}
          reportScope={!selectedSourceState}
          chapterNumber={
            selectedSourceState ? answerChapters[answerKeyOf(selectedSourceState.input)] : undefined
          }
          initialSelection={
            selectedQuote
              ? { sourceKey: selectedQuote.source.key, quoteIndex: selectedQuote.quoteIndex }
              : selectedTableSource
              ? { sourceKey: selectedTableSource.source.key }
              : undefined
          }
          tableReferences={selectedEvidence.tableReferences}
          quoteReferences={selectedEvidence.references}
          open
          question={selectedSourceState?.input.question ?? model.title}
          answerEvidence={selectedEvidence.evidence}
          researchContext={model.researchContext}
          researchStatus={model.researchContextStatus}
          onClose={() => {
            setPanel(null);
            setQuoteReferenceKey(null);
          }}
          loadSourcePassage={loadSourcePassage}
          onNavigateToAnswer={navigateToAnswer}
          sourceUses={sourceUses}
          auditPanel={
            model.audit && model.audit.status !== 'unavailable'
              ? {
                  audit: model.audit,
                  answerKey: selectedSourceState
                    ? answerKeyOf(selectedSourceState.input)
                    : undefined,
                  answerLabels,
                  answerChapters,
                  quoteReferencesByAnswer: quoteReferences,
                  onOpenAnswer: navigateToAnswer,
                }
              : undefined
          }
        />
      ) : null}
    </Box>
  );
}

export default AdvisoryBriefDocument;
